import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api.js';

interface DecisionRow {
  id: string;
  asset: string;
  action: string;
  side: string | null;
  rationale: string;
  confidence: number;
  tick_at: number;
  validation_ok: 0 | 1;
}

interface TradeRow {
  id: string;
  mode: 'paper' | 'live';
  asset: string;
  side: string;
  size: number;
  leverage: number;
  entry_price: number | null;
  exit_price: number | null;
  pnl_usd: number | null;
  status: string;
  opened_at: number;
  closed_at: number | null;
}

interface HistoryResponse {
  decisions: DecisionRow[];
  trades: TradeRow[];
}

const csvEscape = (s: unknown): string => {
  const str = String(s ?? '');
  if (/[,"\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
};

const downloadCsv = (filename: string, header: string[], rows: unknown[][]) => {
  const lines = [header.join(','), ...rows.map((r) => r.map(csvEscape).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const History = () => {
  const [filterMode, setFilterMode] = useState<'all' | 'paper' | 'live'>('all');
  const data = useQuery({
    queryKey: ['history-full'],
    queryFn: () => api.get<HistoryResponse>('/me/history?limit=200'),
  });

  const trades = (data.data?.trades ?? []).filter(
    (t) => filterMode === 'all' || t.mode === filterMode,
  );
  const decisions = data.data?.decisions ?? [];

  const exportDecisions = () => {
    downloadCsv(
      `decisions-${new Date().toISOString().slice(0, 10)}.csv`,
      ['tick_at', 'asset', 'action', 'side', 'confidence', 'validation_ok', 'rationale'],
      decisions.map((d) => [
        new Date(d.tick_at).toISOString(),
        d.asset,
        d.action,
        d.side ?? '',
        d.confidence,
        d.validation_ok,
        d.rationale,
      ]),
    );
  };

  const exportTrades = () => {
    downloadCsv(
      `trades-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        'opened_at',
        'closed_at',
        'mode',
        'asset',
        'side',
        'size',
        'leverage',
        'entry_price',
        'exit_price',
        'pnl_usd',
        'status',
      ],
      trades.map((t) => [
        new Date(t.opened_at).toISOString(),
        t.closed_at ? new Date(t.closed_at).toISOString() : '',
        t.mode,
        t.asset,
        t.side,
        t.size,
        t.leverage,
        t.entry_price ?? '',
        t.exit_price ?? '',
        t.pnl_usd ?? '',
        t.status,
      ]),
    );
  };

  return (
    <main className="page">
      <header className="hero">
        <div>
          <p className="eyebrow">Audit trail</p>
          <h1>Every decision and fill, exportable.</h1>
          <p className="lead">
            Review model rationales, validation state, paper/live fills, and PnL history.
          </p>
        </div>
        <div className="grid">
          <div className="card stat">
            <span className="label">Decisions</span>
            <p className="stat-value">{decisions.length}</p>
          </div>
          <div className="card stat">
            <span className="label">Trades</span>
            <p className="stat-value">{trades.length}</p>
          </div>
        </div>
      </header>

      <section className="panel" style={{ marginTop: '1rem' }}>
        <div className="section-header">
          <h2>AI decisions ({decisions.length})</h2>
          <button className="btn" type="button" onClick={exportDecisions}>
            Export CSV
          </button>
        </div>
        {data.isLoading && <p>Loading…</p>}
        <div className="table-wrap">
          <table style={{ fontSize: '0.9rem' }}>
            <thead>
              <tr>
                <th align="left">Time</th>
                <th align="left">Asset</th>
                <th align="left">Action</th>
                <th align="left">Side</th>
                <th align="left">Conf.</th>
                <th align="left">Rationale</th>
              </tr>
            </thead>
            <tbody>
              {decisions.map((d) => (
                <tr key={`${d.id}-${d.asset}`} style={{ borderTop: '1px solid #eee' }}>
                  <td>{new Date(d.tick_at).toLocaleString()}</td>
                  <td>{d.asset}</td>
                  <td>{d.action}</td>
                  <td>{d.side ?? '—'}</td>
                  <td>{(d.confidence * 100).toFixed(0)}%</td>
                  <td style={{ color: '#555', fontSize: '0.85rem' }}>{d.rationale}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="panel" style={{ marginTop: '1rem' }}>
        <div className="section-header">
          <h2>Trades ({trades.length})</h2>
          <div className="row" style={{ justifyContent: 'flex-start' }}>
            <select
              value={filterMode}
              onChange={(e) => setFilterMode(e.target.value as 'all' | 'paper' | 'live')}
            >
              <option value="all">all</option>
              <option value="paper">paper</option>
              <option value="live">live</option>
            </select>
            <button className="btn" type="button" onClick={exportTrades}>
              Export CSV
            </button>
          </div>
        </div>
        <div className="table-wrap">
          <table style={{ fontSize: '0.9rem' }}>
            <thead>
              <tr>
                <th align="left">Opened</th>
                <th align="left">Mode</th>
                <th align="left">Asset</th>
                <th align="left">Side</th>
                <th align="right">Size</th>
                <th align="right">Lev</th>
                <th align="right">Entry</th>
                <th align="right">Exit</th>
                <th align="right">PnL</th>
                <th align="left">Status</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((t) => (
                <tr key={t.id} style={{ borderTop: '1px solid #eee' }}>
                  <td>{new Date(t.opened_at).toLocaleString()}</td>
                  <td>{t.mode}</td>
                  <td>{t.asset}</td>
                  <td>{t.side}</td>
                  <td align="right">{t.size}</td>
                  <td align="right">{t.leverage}x</td>
                  <td align="right">{t.entry_price ?? '—'}</td>
                  <td align="right">{t.exit_price ?? '—'}</td>
                  <td align="right" style={{ color: (t.pnl_usd ?? 0) >= 0 ? '#0a8' : '#c33' }}>
                    {t.pnl_usd != null ? `$${t.pnl_usd.toFixed(2)}` : '—'}
                  </td>
                  <td>{t.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
};
